// =============================================================================
// ChildShoesSection.jsx
// =============================================================================
// Per-child shoes form section used inside the request wizard.
//
// Renders: two pairs of inputs — joggers (qty + unit cost) and leather shoes
//          (qty + unit cost). Costs default to the standard catalogue but can
//          be overridden by staff. Leather shoes are hidden for primary-school
//          children.
//
// Props:
//   shoes          - { jQty, jCost, lQty, lCost } string/number values
//   onChange       - called with the merged updated shoes object
//   yearLevel      - used to determine primary vs secondary (hides leather shoes)
//   user           - current user; staff see a reimbursement notice
//   standardCosts  - the loaded cost catalogue (defaults to DEFAULT_STANDARD_COSTS)
//   hasError       - if true, highlight the section border as invalid
//
// SEE ALSO
//   src/components/form/ChildSections.jsx — re-exports this component
//   src/utils/formConstants.js            — isPrimary / DEFAULT_STANDARD_COSTS
// =============================================================================
import React from "react";
import { isResponderJob } from "../../app/access";
import { PANEL } from "../../utils/constants";
import { DEFAULT_STANDARD_COSTS, isPrimary } from "../../utils/formConstants";
import { money } from "../../utils/helpers";
import { CostFld } from "./FormUI.jsx";
import { HelpButton } from "./HelpButton.jsx";

function ChildShoesSection({ shoes, onChange, yearLevel, user, standardCosts, hasError }) {
  var isStaff = isResponderJob(user);
  var isPri = isPrimary(yearLevel);
  var costs = standardCosts || DEFAULT_STANDARD_COSTS;
  var jMax = isPri ? costs.joggers_primary || 60 : costs.joggers_secondary || 80;
  var lMax = costs.leather_shoes || 100;

  function up(fields) {
    onChange(Object.assign({}, shoes, fields));
  }

  var jQty = shoes?.jQty || 0;
  var jCost = shoes?.jCost || "";
  var lQty = shoes?.lQty || 0;
  var lCost = shoes?.lCost || "";

  var jSub = jQty * (parseFloat(jCost) || 0);
  var lSub = lQty * (parseFloat(lCost) || 0);
  var total = jSub + lSub;

  var inp = {
    border: "1px solid #CFD8DC",
    borderRadius: 5,
    padding: "5px 8px",
    fontSize: 13,
    fontFamily: "inherit",
    background: "#fff",
    boxSizing: "border-box",
  };

  return (
    <div
      style={{
        border: `1px solid ${hasError ? "#C62828" : "#CFE0DE"}`,
        borderRadius: 10,
        marginBottom: 14,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          background: hasError ? "#FFEBEE" : PANEL.bg,
          borderBottom: `1px solid ${hasError ? "#EF9A9A" : PANEL.border}`,
          padding: "10px 16px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span style={{ fontWeight: 700, fontSize: 14, color: PANEL.heading }}>
          Shoes
          {!isStaff && <HelpButton helpKey="uniforms_shoes" user={user} />}
        </span>
        {total > 0 && (
          <span style={{ fontWeight: 700, color: PANEL.heading, fontSize: 14 }}>
            {money(total)}
          </span>
        )}
      </div>
      <div style={{ padding: "12px 16px" }}>
        {isStaff && (
          <div
            style={{
              background: "#FFF3E0",
              border: "1px solid #FFB300",
              borderRadius: 7,
              padding: "8px 12px",
              marginBottom: 12,
              fontSize: 13,
              color: "#E65100",
            }}
          >
            Shoes are always DFV Service reimbursement — the DFV Service purchases and Zephyr
            reimburses.
          </div>
        )}
        <div style={{ fontSize: 12, color: "#607D79", marginBottom: 12 }}>
          {"Joggers max $" +
            jMax +
            (isPri ? " (primary)" : " (secondary)") +
            (isPri ? "" : `. Leather shoes max $${lMax} — secondary only.`)}
        </div>

        {/* Joggers | Leather side-by-side (single column when leather is hidden for primary). */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isPri ? "1fr" : "1fr 1fr",
            gap: 12,
            alignItems: "start",
          }}
        >
          <div
            style={{
              border: `1px solid ${"#CFE0DE"}`,
              borderRadius: 8,
              padding: "12px 14px",
              marginBottom: 10,
              background: jSub > 0 ? "#F9FBF9" : "transparent",
            }}
          >
            <div style={{ fontWeight: 600, fontSize: 13, color: "#1A2E2B", marginBottom: 8 }}>
              {`Joggers — Max $${jMax}`}
            </div>
            <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
              <div>
                <div style={{ fontSize: 11, color: "#607D79", marginBottom: 3 }}>Qty</div>
                <select
                  value={jQty}
                  onChange={function (e) {
                    up({ jQty: Number(e.target.value) });
                  }}
                  style={Object.assign({}, inp, { width: 70 })}
                >
                  {[0, 1].map(function (n) {
                    return React.createElement("option", { key: n, value: n }, n);
                  })}
                </select>
              </div>
              <div>
                <div style={{ fontSize: 11, color: "#607D79", marginBottom: 3 }}>
                  Cost per pair ($)
                </div>
                <CostFld
                  value={jCost}
                  onChange={function (v) {
                    up({ jCost: v });
                  }}
                  max={jMax}
                  label=""
                />
              </div>
              {jSub > 0 && (
                <div style={{ fontSize: 13, fontWeight: 600, color: "#00695C", marginTop: 14 }}>
                  {money(jSub)}
                </div>
              )}
            </div>
          </div>

          {!isPri && (
            <div
              style={{
                border: `1px solid ${"#CFE0DE"}`,
                borderRadius: 8,
                padding: "12px 14px",
                marginBottom: 10,
                background: lSub > 0 ? "#F9FBF9" : "transparent",
              }}
            >
              <div style={{ fontWeight: 600, fontSize: 13, color: "#1A2E2B", marginBottom: 8 }}>
                {`Leather Shoes — Max $${lMax}`}
              </div>
              <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                <div>
                  <div style={{ fontSize: 11, color: "#607D79", marginBottom: 3 }}>Qty</div>
                  <select
                    value={lQty}
                    onChange={function (e) {
                      up({ lQty: Number(e.target.value) });
                    }}
                    style={Object.assign({}, inp, { width: 70 })}
                  >
                    {[0, 1].map(function (n) {
                      return React.createElement("option", { key: n, value: n }, n);
                    })}
                  </select>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: "#607D79", marginBottom: 3 }}>
                    Cost per pair ($)
                  </div>
                  <CostFld
                    value={lCost}
                    onChange={function (v) {
                      up({ lCost: v });
                    }}
                    max={lMax}
                    label=""
                  />
                </div>
                {lSub > 0 && (
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#00695C", marginTop: 14 }}>
                    {money(lSub)}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {isPri && isStaff && (
          <div style={{ fontSize: 12, color: "#607D79", fontStyle: "italic" }}>
            Leather shoes not available for primary school students.
          </div>
        )}

        {total > 0 && (
          <div
            style={{
              textAlign: "right",
              fontWeight: 700,
              color: "#00695C",
              fontSize: 14,
              marginTop: 8,
              paddingTop: 8,
              borderTop: `1px solid ${"#CFE0DE"}`,
            }}
          >
            {`Shoes total: ${money(total)}`}
          </div>
        )}
      </div>
    </div>
  );
}

export { ChildShoesSection };
